import {
    Box,
    Typography,
    Link,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useTranslation } from "react-i18next";
import { CanonicalStaticTag } from "../Components/Header/canonicaltags";

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="h3" gutterBottom component="div">
        <strong>{children}</strong>
    </Typography>
);


const SectionSubHeading = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle1" // smaller than heading
        gutterBottom
        component="div"
        sx={{ mb: 1.5, fontWeight: "bold" }}
    >
        {children}
    </Typography>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ mb: 1.5 }}>
        {children}
    </Typography>
);

const BulletList = ({ items }: { items: React.ReactNode[] }) => (
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        {items.map((item, index) => (
            <Typography
                key={index}
                component="li"
                sx={{ mb: 0.5, display: "list-item", listStyleType: "disc", fontSize: '12.8px' }}
            >
                {item}
            </Typography>
        ))}
    </Box>
);

export const ToolBoxLegalNotice = () => {
    const { t } = useTranslation("common");

    return (
        <>
            <CanonicalStaticTag page="/legal-policy" />
            <Box className="main-container toolbox-legal-notice-container">
                <Typography component="div" className="space-from-top" />
                <Typography component="h2" className="heading-two">
                    Saint-Gobain BIM Toolbox -Terms of use
                </Typography>

                {/* ----------------- Section 0: Definitions ----------------- */}
                <SectionHeading>Definitions</SectionHeading>
                <Paragraph>
                    <strong>Affiliate</strong>: shall mean any present or future entity which is directly or indirectly Controlled by Compagnie de Saint-Gobain. <strong>Controls</strong>, <strong>Controlled</strong> or <strong>Control</strong> means the ownership or holding of at least fifty percent (50%) of the share capital or voting rights of such company.
                </Paragraph>
                <Paragraph>
                    <strong>Application</strong>: {" "} shall mean together, the BIM ToolBox and the Plugins.
                </Paragraph>
                <Paragraph>
                    <strong>Authorized User</strong>: {" "} shall mean an employee or provider of an Affiliate of the Saint-Gobain Group authorized by GDI, thought an identity and access management solution (OKTA), to access the BIM ToolBox. An Authorized User can also be an external user outside of the Saint-Gobain Group.
                </Paragraph>
                <Paragraph>
                    <strong>BIM ToolBox</strong>: {" "}shall mean the Building Information Management Tool that distribute and installs the SG Plugins relating to a construction project in different CAD software.
                </Paragraph>
                <Paragraph>
                    <strong>Divested Affiliates</strong>: shall mean an Affiliate leaving the Saint-Gobain Group on a specific date.
                </Paragraph>
                <Paragraph>
                    <strong>Plugin</strong>: {" "}shall mean a CAD addon from different brands of SG, the provides BIM-related functionalities involving injection or retrieval of construction data between a BIM model and SG, execute different geometrical calculations, documents generation.
                </Paragraph>
                <Paragraph>
                    <strong>SG</strong>, <strong>GDI</strong> or <strong>Us </strong> shall mean Saint-Gobain Group Digital & IT International.
                </Paragraph>
                <Paragraph>
                    <strong>ToU </strong>shall mean the present Terms of Use.
                </Paragraph>
                <Paragraph>
                    <strong>You</strong> or <strong>Your </strong>shall mean you signing and accepting the ToU as Authorized User.
                </Paragraph>

                {/* ----------------- Section 1 ----------------- */}
                <SectionHeading>1. Acknowledgment</SectionHeading>
                <Paragraph>
                    These Terms of Use set out the rights and obligations of all Authorized Users regarding the use of the Application.
                </Paragraph>
                <Paragraph>
                    Therefore, Your general terms and conditions are hereby excluded, as well as all other documents, including if it is mentioned, in the form of URLs.
                </Paragraph>
                <Paragraph>
                    Your access to and use of the BIM ToolBox is conditioned on Your acceptance of and compliance with these ToU. These ToU apply to all Authorized Users who have been granted an access by GDI to use the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    By accessing or using the BIM ToolBox, You agree to be bound by these ToU. If You disagree with any part of these ToU then You may not access nor use the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    You guarantee that you are over the age of 18 and You are an employee or provider of an Affiliate of Saint-Gobain Group authorized by GDI to access the Application.
                </Paragraph>

                {/* ----------------- Section 2 ----------------- */}
                <SectionHeading>2. Access to the Application</SectionHeading>
                <Paragraph>
                    When You ask for an access to the Application and create an account on it, You must provide to GDI information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the ToU, which may result in immediate termination of Your account on the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    You must notify Us immediately upon becoming aware of any breach of security or unauthorized use of Your account.
                </Paragraph>

                {/* ----------------- Section 3 ----------------- */}
                <SectionHeading>
                    <strong>3. Plugin</strong>
                </SectionHeading>
                <Paragraph>
                    With the BIM Toolbox installed, you will get access to plugins from different SG brands. Some of these plugins will not require a paid license, while other will. Different SG business will be in in charge of providing additional information for each plugin, such as user guides, ToU and more, either through the plugin itself or some other means.
                </Paragraph>

                {/* ----------------- Section 4 ----------------- */}
                <SectionHeading>
                    <strong>
                        4. Conditions of use
                    </strong>
                </SectionHeading>
                <Paragraph>
                    It should be noted by You that the BIM ToolBox in no way relieves the Authorized User of its responsibilities.
                </Paragraph>
                <Paragraph>
                    Failure to comply with the conditions of access or use set out below will result in GDI closing of the concerned Playground and banning of Your access to the Application.
                </Paragraph>

                {/* ----------------- Section 4.1 ----------------- */}
                <SectionSubHeading>
                    <strong>
                        4.1 Unauthorized use
                    </strong>
                </SectionSubHeading>
                <Paragraph>
                    Neither Authorized User/You may use the Application:
                </Paragraph>
                <BulletList
                    items={[
                        "In a way prohibited by law, regulation, governmental order, or decree.",
                        "To violate the rights of others.",
                        "To try to gain unauthorized access to or disrupt any service, device, data, account, or network.",
                        "To spam or distribute malware.",
                        "To mine cryptocurrency.",
                        "In a way that could harm the Application or impair anyone else’s use of it.",
                        "In any application or situation where failure of the BIM ToolBox could lead to the death or serious bodily injury of any person, or to severe physical or environmental damage, except in accordance with the High-Risk Use section below",
                        "To assist of encourage anyone to do any of the above."
                    ]}
                />
                <Paragraph>
                    You may not use the BIM ToolBox to discover any underlying components of the models, algorithms, and systems, such as exfiltrating the weights of models.
                </Paragraph>
                <Paragraph>
                    You may not use web scraping, web harvesting, or web data extraction methods to extract data from the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    Violation of these ToU in this section may result in suspension of the access to the BIM ToolBox.
                </Paragraph>

                {/* ----------------- Section 4.2 ----------------- */}
                <SectionSubHeading>
                    <strong>
                        4.2 Code of conduct
                    </strong>
                </SectionSubHeading>
                <Paragraph>
                    It is not permitted to:
                </Paragraph>
                <BulletList
                    items={[
                        "Make decisions without appropriate human oversight if your application may have a consequential impact on any individual’s legal position, financial position, life opportunities, employment opportunities, human rights, or result in physical or psychological injury to an individual.",
                        "Infer sensitive information about people without their explicit consent unless if used in a lawful manner by a law enforcement entity, court, or government official subject to judicial oversight in a jurisdiction that maintains a fair and independent judiciary authority."
                    ]}
                />

                {/* ----------------- Section 4.3 ----------------- */}
                <SectionSubHeading>4.3 Use limitations</SectionSubHeading>
                <Paragraph>
                    It is specified that both internal and external use must respect the principles of confidentiality and the third parties’ right.
                </Paragraph>
                <Paragraph>
                    External use is more controlled regarding the high risk of infringement of third-party rights.
                </Paragraph>
                <Paragraph>
                    Only the Use Cases for external use listed by GDI (in the Acceptance Form) are authorized without any specific control.
                </Paragraph>

                {/* ----------------- Section 4.4 ----------------- */}
                <SectionSubHeading>4.4 High-risk use</SectionSubHeading>
                <Paragraph>
                    The BIM ToolBox are not designed or intended to support any use in which a service interruption, defect, error, or other failure of an online service could result in the death or serious bodily injury of any person or in physical or environmental damage (collectively, “High-Risk Use”).
                </Paragraph>
                <Paragraph>
                    You agree to defend, indemnify, and hold GDI harmless from and against all damages, costs and attorneys' fees in connection with any claims arising from a High-Risk Use associated with the BIM ToolBox.
                </Paragraph>

                {/* ----------------- Section 4.5 ----------------- */}
                <SectionSubHeading>4.5 Medical disclaimer</SectionSubHeading>
                <Paragraph>
                    You acknowledge that the BIM ToolBox (1) is not designed, intended, or made available as a medical device(s), and (2) is not designed or intended to be a substitute for professional medical advice, diagnosis, treatment, or judgment and should not be used to replace or as a substitute for professional medical advice, diagnosis, treatment, or judgment.
                </Paragraph>

                {/* ----------------- Section 5 ----------------- */}
                <SectionHeading>5. Intellectual Property Rights </SectionHeading>
                <Paragraph>
                    The BIM ToolBox and the Plugins and their respective documentation, as well as any copy, remain the exclusive property of GDI, which reserves authorship in accordance with the provisions of the intellectual property code. The BIM ToolBox and each Plugin may not be assigned, contributed, or transferred without the agreement of GDI.
                </Paragraph>
                <Paragraph>
                    You undertake not to develop or market a likely to compete with it.
                </Paragraph>
                <Paragraph>
                    You may not modify the BIM ToolBox or Plugin, nor adapt it without the prior express written authorization of GDI.
                </Paragraph>
                <Paragraph>
                    The BIM ToolBox and the Plugin and their original content, features and functionality are and will remain the exclusive property of GDI.
                </Paragraph>

                {/* ----------------- Section 6 ----------------- */}
                <SectionHeading>6. Confidential information</SectionHeading>
                <Paragraph>
                    "Confidential Information" shall mean all information/data, regarded as confidential, disclosed, transmitted, or made available by the Authorized User, into the Application, through Inputs, during the use of the Playground.
                </Paragraph>
                <Paragraph>
                    Use of Confidential Information on the BIM ToolBox and the Plugin is at the Authorized User's own risk.
                </Paragraph>

                {/* ----------------- Section 7 ----------------- */}
                <SectionHeading>7. Personal data</SectionHeading>
                <Paragraph>
                    To be granted access to the BIM Toolbox, you need to create an account providing: you full name, email address, country, and company name as mandatory elements. optionally you can complete your profile with your city name, postal code, address, phone number and job title.
                </Paragraph>
                <Paragraph>
                    This information may be used by different business within Saint-Gobain to provide technical assistance.
                </Paragraph>
                <Paragraph>
                    Upon creation of the account, you have the option to opt-out of any marketing-related communication or campaigns.
                </Paragraph>
                <Paragraph>
                    Additionally, different events and actions performed by you with SG plugins will be traced. Anonymous information will enrich different metrics on the plugin usage, while personal information will be sent to SG brands to better understand user behavior on the plugins such as preferred products, usage habits and more.
                </Paragraph>

                {/* ----------------- Section 8 ----------------- */}
                <SectionHeading>8. Links to other websites </SectionHeading>
                <Paragraph>
                    BIM ToolBox and Plugin may contain links to third-party web sites or services that are not owned or controlled by GDI.
                </Paragraph>
                <Paragraph>
                    GDI has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party web sites or services.
                </Paragraph>
                <Paragraph>
                    You further acknowledge and agree that GDI shall not be responsible or liable, directly, or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such web sites or services.
                </Paragraph>
                <Paragraph>
                    GDI strongly advise You to read the terms and conditions and privacy policies of any third-party web sites or services that You visit.
                </Paragraph>

                {/* ----------------- Section 9 ----------------- */}
                <SectionHeading>9. Changes to these TOU and termination</SectionHeading>
                <Paragraph>
                    GDI reserves the right, at its sole discretion, to modify or replace these ToU at any time. If a revision is material, GDI will make reasonable efforts to provide at least thirty (30) days' notice prior to any new terms taking effect. What constitutes a material change will be determined at GDI sole discretion.
                </Paragraph>
                <Paragraph>
                    By continuing to access or use the BIM ToolBox after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    We may terminate or suspend Your access to the BIM ToolBox immediately, without prior notice or liability, if GDI suspects breach of these ToU or if You breach these ToU.
                </Paragraph>
                <Paragraph>
                    Upon termination, Your right to use the Application will cease immediately.
                </Paragraph>

                {/* ----------------- Section 10 ----------------- */}
                <SectionHeading>10. Disclaimer </SectionHeading>
                <Paragraph>
                    It is expressly acknowledged and agreed that, to the extent permitted by applicable law, use of the BIM ToolBox is at sole risk of the Authorized User.
                </Paragraph>
                <Paragraph>
                    To the maximum extent permitted by applicable law, the BIM ToolBox is provided “as is” and “as available”, with all faults and without warranty of any kind.
                </Paragraph>
                <Paragraph>
                    GDI hereby disclaim all warranties and conditions with respect to the BIM ToolBox, either express, implied, or statutory, including but not limited to the implied warranties and/or conditions of merchantability, satisfactory quality, fitness for a particular purpose, accuracy, quiet enjoyment, and non-infringement of third-party rights.
                </Paragraph>
                <Paragraph>
                    Should the BIM ToolBox, infringe any third-party rights and/or cause any direct and indirect damages including but not limited to intellectual, physical, material, immaterial, body injuries to anyone, Authorized User assumes all entire liability and entire costs.
                </Paragraph>
                <Paragraph>
                    GDI does not warrant the BIM ToolBox, (i) will meet requirements of Authorized User, (ii) will be uninterrupted or error-free, (iii) will be compatible or work with any third-party software, applications, or third-party services, or (iv) that defects in the BIM ToolBox will be corrected.
                </Paragraph>
                <Paragraph>
                    No oral or written information or advice given by GDI or GDI authorized representative shall create a warranty.
                </Paragraph>

                {/* ----------------- Section 11 ----------------- */}
                <SectionHeading> 11. Liability of Authorized Users </SectionHeading>
                <Paragraph>
                    Authorized Users and the Affiliates to which they belong are responsible for any direct, material, and immaterial, damage or loss they caused or alleged to be caused in connection with GDI and the Saint-Gobain Group and its Affiliates, in the context of the use or misuse of the BIM ToolBox, and in the event of failure to respect the intellectual property rights of GDI, Affiliates and any third party.
                </Paragraph>

                {/* ----------------- Section 12 ----------------- */}
                <SectionHeading>  12. Limit of Liability </SectionHeading>
                <Paragraph>
                    GDI shall not be responsible or liable, directly, or indirectly, for any damages or loss (including direct, indirect, incidental, consequential and any and all other forms of damages or losses regardless of the form of the action or the basis of the claim) caused or alleged to be caused in connection with Your use of the BIM ToolBox.
                </Paragraph>
                <Paragraph>
                    In no event shall GDI’s total liability to You and any Authorized User for the use of the BIM ToolBox for any and all damages exceed the amount of one thousand euros (1,000.00€).
                </Paragraph>
                <Paragraph>
                    To clarify, GDI do not limit or exclude its liability:
                </Paragraph>
                <BulletList
                    items={[
                        "For personal injury or death resulting from our negligence.",
                        "For fraud or fraudulent misrepresentation.",
                        "To the extent our liability cannot be limited or excluded under applicable law.",
                    ]}
                />

                {/* ----------------- Section 13 ----------------- */}
                <SectionHeading>
                    13. Divested Affiliates
                </ SectionHeading>
                <Paragraph>
                    In the event of a Divested Affiliate leaves the Saint-Gobain Group, the Divested Affiliate would no longer have access to the BIM ToolBox.
                </Paragraph>

                {/* ----------------- Section 14 ----------------- */}
                <SectionHeading>
                    14. Governing Law and dispute resolution
                </ SectionHeading>
                <Paragraph>
                    The laws of France, excluding its conflicts of law rules, shall govern this ToU and Your use of the Application.
                </Paragraph>
                <Paragraph>
                    If You have any concern or dispute about the Application, You agree to first try to resolve the dispute informally by contacting GDI at the address referenced below:
                </Paragraph>
                <Paragraph>
                    <Link
                        rel="noopener"
                        href="mailto:digitalconstructionsolutions@saint-gobain.com"
                        target="_blank"
                    >
                        digitalconstructionsolutions@saint-gobain.com
                    </Link>
                    {" "}
                </Paragraph>
                <Paragraph>
                    Any litigation must be brought before the competent courts of Paris, France.
                </Paragraph>

                {/* ----------------- Section 15 ----------------- */}
                <SectionHeading>15. Miscellaneous</ SectionHeading>
                <SectionSubHeading>
                    15.1 Entire Agreement
                </ SectionSubHeading>
                <Paragraph>
                    These ToU constitute the entire agreement of the parties hereto with respect to the subject matter hereof and supersede all prior agreements and undertakings, both written and oral, between the parties with respect to the subject matter hereof.
                </Paragraph>
                <SectionSubHeading>
                    15.2 Severability
                </ SectionSubHeading>
                <Paragraph>
                    If any provision of these ToU is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
                </Paragraph>
                <SectionSubHeading>
                    15.3 Waiver
                </ SectionSubHeading>
                <Paragraph>
                    Except as provided herein, the failure to exercise a right or to require performance of an obligation under these ToU shall not affect a party's ability to exercise such right or require such performance at any time thereafter nor shall the waiver of a breach constitute a waiver of any subsequent breach.
                </Paragraph>
            </Box >
        </>
    );
};
